/**
 * Created by andreaskarantzas on 15.05.18.
 */
import { reaction, runInAction, makeAutoObservable } from 'mobx';
import { AthleteConfiguration, AthleteConfigurationApi, createAthleteConfiguration } from '../../Model/Athlete/AthleteConfiguration';
import { AuthenticationStore } from '../../Store/AuthenticationStore';

/**
 * Used for user specific settings (device independent) -> Global just for users.
 * e.g. language settings
 */
function createRemoteSettingsStore(authentication: AuthenticationStore) {
  const store = makeAutoObservable({
    authentication,
    loaded: false,
    config: undefined as AthleteConfiguration | undefined,

    clear() {
      this.config = createAthleteConfiguration();
      this.loaded = false;
    },

    loadSettings() {
      return AthleteConfigurationApi.get()
        .then((config) => {
          runInAction(() => (this.config = config));
          return config;
        })
        .then(() => runInAction(() => (this.loaded = true)));
    },

    remove(key: string) {
      if (this.config) {
        delete this.config.data[key];
      }
      return this.config?.save() ?? Promise.resolve(createAthleteConfiguration());
    },

    saveSetting(key: string, value: unknown) {
      if (this.config) {
        this.config.data[key] = value;
      }
      return this.config?.save() ?? Promise.resolve(createAthleteConfiguration());
    },

    setMany(keyValue: Record<string, unknown>) {
      Array.from(Object.entries(keyValue)).forEach(([key, value]) => {
        if (this.config) {
          this.config.data[key] = value;
        }
      });
      return this.config?.save() ?? Promise.resolve(createAthleteConfiguration());
    },

    set(key: string, value: any) {
      return this.saveSetting(key, value);
    },

    get(key: string, defaultValue?: any): any {
      return this.config?.data[key] ?? defaultValue;
    },

    async delete(): Promise<any> {
      return Promise.resolve();
    },
  });

  reaction(
    () => authentication.athlete,
    (athlete) => {
      if (athlete) {
        store.loadSettings();
      } else {
        store.clear();
      }
    },
    { fireImmediately: true, name: 'Firebase get app preferences' },
  );

  return store;
}

export type RemoteSettingsStore = ReturnType<typeof createRemoteSettingsStore>;
export const createRemoteSettingsStoreInstance = createRemoteSettingsStore;
